import React, { useEffect, useRef } from "react";
import { Box, Text, Flex, Input, Select, Button, Textarea } from "components-lib";
import { CosIcon } from "hugeicons-react";

/**
 * NextVisitSection Component
 * 
 * Manages next visit scheduling, doctor referrals, investigations, and past medication
 * Provides form inputs for scheduling follow-up appointments and referral information
 * Features automatic date calculation based on selected duration from today's date
 */
const NextVisitSection = ({
  templateData,
  nextVisitDuration,
  setNextVisitDuration,
  nextVisitDate,
  setNextVisitDate,
  referredDoctor,
  setReferredDoctor,
  referredSpeciality,
  setReferredSpeciality,
  referredPhone,
  setReferredPhone,
  referredEmail,
  setReferredEmail,
  referredHospital,
  setReferredHospital,
  referredHospitalAddress,
  setReferredHospitalAddress,
  investigation,
  setInvestigation,
  investigationsList,
  setInvestigationsList,
  pastMedication,
  setPastMedication,
  onAddInvestigation,
  onDeleteInvestigation,
  onNextVisitDurationChange,
  appointmentId,
  appointmentPrescription,
}) => {


  const hydratedFromPrescriptionRef = useRef(null);


  // useEffect(() => {
  //   alert(`NextVisitdate: ${nextVisitDate}`);
  // }, [nextVisitDate]);

  useEffect(() => {
    if (!appointmentId || !appointmentPrescription) return;
    if (hydratedFromPrescriptionRef.current === appointmentId) return;

    try {
      const source = appointmentPrescription.data || appointmentPrescription;
      const prescription = source?.prescription ?? source ?? {};

      if (prescription?.next_visit) {
        setNextVisitDate(prescription.next_visit);
      }

      if (prescription?.investigation) {
        const investigations = prescription.investigation.split(',').map((inv) => inv.trim()).filter(Boolean);
        setInvestigationsList(investigations);
      }

      if (prescription?.past_medication) {
        setPastMedication(prescription.past_medication);
      }

      const referrals = source?.referred_to || prescription?.referred_to || [];
      if (Array.isArray(referrals) && referrals.length > 0) {
        const referred = referrals[0];
        setReferredDoctor(referred.referred_doctor_name || '');
        setReferredHospital(referred.hospital || '');
        setReferredHospitalAddress(referred.hospital_address || '');
        setReferredPhone(referred.phone || '');
        setReferredEmail(referred.email || '');
        setReferredSpeciality(referred.speciality || referred.referred_doctor_speciality || '');
      }

      hydratedFromPrescriptionRef.current = appointmentId;
    } catch (error) {
      console.error(
        `Error hydrating next visit data for appointment ID ${appointmentId}:`,
        error,
      );
    }
  }, [appointmentId, appointmentPrescription, setInvestigationsList, setNextVisitDate, setPastMedication, setReferredDoctor, setReferredEmail, setReferredHospital, setReferredHospitalAddress, setReferredPhone, setReferredSpeciality]);

  useEffect(() => {
    if (!templateData) return;

    try {
      let parsed = templateData;
      if (typeof templateData === "string") {
        parsed = JSON.parse(templateData);
      }

      // Handle next visit date from template
      if (parsed?.next_visit) {
        setNextVisitDate(parsed.next_visit);
      }

      // Handle investigations from template
      if (parsed?.investigation) {
        console.log("INvestigation", parsed?.investigation)
        const templateInvestigations = parsed.investigation
          .split(',')
          .map(inv => inv.trim())
          .filter(inv => inv);

        setInvestigationsList((prev = []) => {
          const existing = Array.isArray(prev) ? prev : [];
          const toAdd = templateInvestigations.filter(inv =>
            !existing.some(existingInv =>
              existingInv.toLowerCase() === inv.toLowerCase()
            )
          );
          return [...existing, ...toAdd];
        });
      }

      // Handle past medication from template
      if (parsed?.past_medication) {
        setPastMedication(parsed.past_medication);
      }

      // Handle referred to information from template (map all available fields)
      if (parsed.referred_to && Array.isArray(parsed.referred_to) && parsed.referred_to.length > 0) {
        const referred = parsed.referred_to[0];

        // Basic referred contact/info fields (use available setters, prefer doctor-specific fields when present)
        setReferredDoctor(referred.referred_doctor_name ?? "");
        setReferredSpeciality(referred.speciality ?? "");
        setReferredHospital(referred.hospital ?? "");
        setReferredHospitalAddress(referred.hospital_address ?? "");
        setReferredPhone(referred.doctor_phone ?? referred.phone ?? "");
        setReferredEmail(referred.doctor_email ?? referred.email ?? "");

        // If referral includes a date, apply it (treat as referral date / next visit as appropriate)
        if (referred.date) {
          setNextVisitDate(referred.date);
        }

        // Log other metadata fields that this component does not have setters for (id, doctor_id, appointment_id, prescription_id)
        if (referred.id || referred.doctor_id || referred.appointment_id || referred.prescription_id) {
          // keep out of user-visible UI but helpful for debugging
          // eslint-disable-next-line no-console
          console.debug("Referred metadata from template:", {
            id: referred.id,
            doctor_id: referred.doctor_id,
            appointment_id: referred.appointment_id,
            prescription_id: referred.prescription_id,
          });
        }
      }

    } catch (err) {
      console.warn("Failed to parse/apply templateData for NextVisitSection:", err);
    }
  }, [
    templateData,
    setNextVisitDate,
    setInvestigationsList,
    setPastMedication,
    setReferredDoctor,
    setReferredHospital,
    setReferredHospitalAddress,
    setReferredPhone, setReferredSpeciality,
    setReferredEmail
  ]);


  return (
    <Box 
      borderWidth="1px" 
      borderColor="gray.200" 
      // borderRadius="12px"
      p={{ base: "20px", md: "28px" }}
      bg="white"
      boxShadow="sm"
      mb={6}
    >
      {/* Next Visit Section */}
      <Text fontSize="xl" fontWeight="bold" mb={6} color="gray.700">Next Visit</Text>
      <Flex mb={8} alignItems="flex-start" gap={6} flexWrap="wrap">
        {/* Duration Selection */}
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={3}>Select Duration:</Text>
          <Select
            // placeholder="Next Visit After"
            value={nextVisitDuration}
            onChange={onNextVisitDurationChange}
            size="md"
            borderRadius="md"
            w="220px"
          >
            <option value="Next Visit After">Next Visit After</option>
            <option value="1 day">1 day</option>
            <option value="2 days">2 days</option>
            <option value="3 days">3 days</option>
            <option value="4 days">4 days</option>
            <option value="5 days">5 days</option>
            <option value="6 days">6 days</option>
            <option value="1 week">1 week</option>
            <option value="2 weeks">2 weeks</option>
            <option value="3 weeks">3 weeks</option>
            <option value="1 month">1 month</option>
            <option value="1.5 month">1.5 month</option>
            <option value="2 months">2 months</option>
            <option value="3 months">3 months</option>
            <option value="6 months">6 months</option>
          </Select>
        </Box>

        {/* Auto-calculated Date Display  */}
        {nextVisitDate && nextVisitDate !== "Next Visit After" ? (
          <Box>
            <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={3}>Next Visit Date:</Text>
            <Box
              bg="green.50"
              border="2px solid"
              borderColor="green.200"
              borderRadius="md"
              px="4"
              py="2"
              minWidth="150px"
              textAlign="center"
            >
              <Text
                fontSize="md"
                color="green.700"
                fontWeight="bold"
              >
                {new Date(nextVisitDate).toLocaleDateString('en-US', {
                  weekday: 'short',
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </Text>
              <Text fontSize="xs" color="green.600" mt="1">
                ({nextVisitDuration} from today)
              </Text>
            </Box>
          </Box>
        ) : (
          setNextVisitDate(null)
        )}

        {/* Manual Date Override */}
        <Box>
          <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={3}>Override Date (Optional):</Text>
          <Input
            type="date"
            value={nextVisitDate}
            onChange={(e) => setNextVisitDate(e.target.value)}
            size="md"
            borderRadius="md"
            w="220px"
            bg={nextVisitDuration ? "yellow.50" : "white"}
            title="You can manually override the auto-calculated date if needed"
          />
        </Box>
      </Flex>

      {/* Referred To Section */}
      <Text fontSize="xl" fontWeight="bold" mb={6} mt={8} color="gray.700">Referred To:</Text>
      <Flex direction="column" mb={6} gap={4}>
        <Flex direction="row" gap={4} flexWrap="wrap">
          <Input
            placeholder="Doctor Name"
            value={referredDoctor}
            onChange={(e) => setReferredDoctor(e.target.value)}
            size="md"
            borderRadius="md"
            flex="1"
            minW="200px"
          />
          <Input
            placeholder="Speciality"
            value={referredSpeciality}
            onChange={(e) => setReferredSpeciality(e.target.value)}
            size="md"
            borderRadius="md"
            flex="1"
            minW="200px"
          />
          <Input
            placeholder="Doctor Phone"
            value={referredPhone}
            onChange={(e) => setReferredPhone(e.target.value)}
            size="md"
            borderRadius="md"
            flex="1"
            minW="200px"
          />
          <Input
            placeholder="Doctor Email"
            value={referredEmail}
            onChange={(e) => setReferredEmail(e.target.value)}
            size="md"
            borderRadius="md"
            flex="1"
            minW="200px"
          />
        </Flex>
        <Flex direction="row" gap={4} flexWrap="wrap">
          <Input
            placeholder="Hospital Name"
            value={referredHospital}
            onChange={(e) => setReferredHospital(e.target.value)}
            size="md"
            borderRadius="md"
            flex="1"
            minW="200px"
          />
          <Input
            placeholder="Hospital Address"
            value={referredHospitalAddress}
            onChange={(e) => setReferredHospitalAddress(e.target.value)}
            size="md"
            borderRadius="md"
            flex="1"
            minW="200px"
          />
        </Flex>
      </Flex>

      {/* Investigation Section */}
      <Text fontSize="xl" fontWeight="bold" mb={6} mt={8} color="gray.700">Investigation:</Text>

      {/* Display added investigations */}
      {investigationsList.length > 0 && (
        <Box mb={6}>
          {investigationsList.map((item, index) => (
            <Flex key={index} align="center" mb={3} p={4} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
              <Text flex="1" fontSize="md">{item}</Text>
              <Button
                size="sm"
                colorScheme="red"
                border="none"
                fontSize="16px"
                fontWeight="bold"
                variant="ghost"
                onClick={() => onDeleteInvestigation(index)}
              >
                X
              </Button>
            </Flex>
          ))}
        </Box>
      )}

      {/* Add Investigation Input */}
      <Flex mb={6} gap={4}>
        <Input
          borderRadius="md"
          placeholder="Investigation Details"
          value={investigation}
          onChange={(e) => setInvestigation(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              onAddInvestigation();
            }
          }}
          size="md"
          flex="1"
        />
        <Button
          onClick={onAddInvestigation}
          background="#4CAF50"
          size="md"
          
          fontWeight="semibold"
        >
          Add
        </Button>
      </Flex>

      {/* Past Medication Section */}
      <Text fontSize="xl" fontWeight="bold" mb={4} mt={8} color="gray.700">Past Medication:</Text>
      <Textarea
        borderRadius="md"
        borderColor="gray.200"
        placeholder="Enter past medication details"
        value={pastMedication}
        onChange={(e) => setPastMedication(e.target.value)}
        size="md"
        minH="120px"
        _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
      />
    </Box>
  );
};

export default NextVisitSection;